const API_BASE = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('token');
  }

  async request(endpoint, options = {}) {
    const headers = { 'Content-Type': 'application/json' };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: { ...headers, ...options.headers },
    });

    if (response.status === 401) {
      this.clearToken();
      window.location.href = '/login';
      return;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      let message = 'Request failed';
      if (typeof error.detail === 'string') {
        message = error.detail;
      } else if (Array.isArray(error.detail)) {
        message = error.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      }
      throw new Error(message);
    }

    return response.json();
  }

  // Auth
  async login(email, password) {
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.access_token);
    return data;
  }

  async register(email, password, fullName, businessName, extras = {}) {
    const data = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        full_name: fullName,
        business_name: businessName,
        business_type: extras.businessType || null,
        business_description: extras.businessDescription || null,
        business_category: extras.businessCategory || 'local_business',
        owner_first_name: extras.ownerFirstName || null,
        response_tone: extras.responseTone || 'friendly, professional, warm',
        currency: extras.currency || 'gbp',
        self_reported_rating: extras.selfReportedRating ?? null,
        self_reported_reviews: extras.selfReportedReviews ?? null,
      }),
    });
    this.setToken(data.access_token);
    return data;
  }

  async forgotPassword(email) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token, new_password) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password }),
    });
  }

  // Account
  async deleteAccount(password, confirmation) {
    return this.request('/account/delete', {
      method: 'POST',
      body: JSON.stringify({ password, confirmation }),
    });
  }

  async exportAccountData() {
    return this.request('/account/export');
  }

  // Businesses
  async getBusinesses() {
    return this.request('/businesses');
  }

  // Platform connections
  async getConnections() {
    return this.request('/platforms/connections');
  }

  async getGoogleAuthUrl() {
    return this.request('/platforms/google/auth-url');
  }

  async disconnectConnection(connectionId) {
    return this.request(`/platforms/connections/${connectionId}`, { method: 'DELETE' });
  }

  // Reviews
  async getReviews(status = null) {
    const businesses = await this.getBusinesses();
    if (businesses.length === 0) return [];
    let query = `?business_id=${businesses[0].id}`;
    if (status) query += `&status=${status}`;
    return this.request(`/reviews${query}`);
  }

  async addManualReview(reviewerName, rating, reviewText, platform = 'google', reviewDate = null, autoGenerate = true) {
    const businesses = await this.getBusinesses();
    if (businesses.length === 0) throw new Error('No business found');
    return this.request('/reviews/manual', {
      method: 'POST',
      body: JSON.stringify({
        business_id: businesses[0].id,
        reviewer_name: reviewerName,
        rating,
        review_text: reviewText || null,
        platform,
        review_date: reviewDate || null,
        auto_generate: autoGenerate,
      }),
    });
  }

  async generateResponse(reviewId) {
    return this.request(`/reviews/${reviewId}/generate`, { method: 'POST' });
  }

  async regenerateResponse(reviewId) {
    return this.request(`/reviews/${reviewId}/regenerate`, { method: 'POST' });
  }

  async approveResponse(reviewId, editedResponse = null) {
    const body = editedResponse ? JSON.stringify({ edited_response: editedResponse }) : '{}';
    return this.request(`/reviews/${reviewId}/approve`, {
      method: 'POST',
      body,
    });
  }

  async skipReview(reviewId) {
    return this.request(`/reviews/${reviewId}/skip`, { method: 'POST' });
  }

  async translateAllReviews() {
    return this.request('/reviews/translate-all', { method: 'POST' });
  }

  // Dashboard
  async getDashboardStats() {
    const businesses = await this.getBusinesses();
    if (businesses.length === 0) return null;
    return this.request(`/dashboard/stats/${businesses[0].id}`);
  }

  // Billing
  async createCheckout() {
    return this.request('/billing/checkout', { method: 'POST' });
  }

  async getMe() {
    return this.request('/auth/me');
  }

  isAuthenticated() {
    return !!this.token;
  }

  async createPortalSession() {
    return this.request('/billing/portal', { method: 'POST' });
  }

  // Settings
  async getBusinessSettings() {
    return this.request('/settings/business');
  }

  async updateBusinessSettings(data) {
    return this.request('/settings/business', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async get(url) {
    return this.request(url);
  }

  async post(url, body) {
    return this.request(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async deleteReview(reviewId) {
    return this.request(`/reviews/${reviewId}`, { method: 'DELETE' });
  }
}

const api = new ApiService();
export default api;
